import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { FormDropdown, type Option } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { type PensionFormType, getPensionFormSchema } from "Forms/Schemas/GoalsFulfillment/CurrentSolution/pensionFormSchema";
import { useGetAnalysisClients } from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  getGetAssetAllocationOverviewQueryKey,
  getListCurrentContractsQueryKey,
  useCreateCurrentContract,
  usePatchCurrentContract,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  CreateCurrentContractPensionSavingBodyInvestmentToolType,
  type CreateCurrentContractPensionSavingBodyProposedStepActionType,
  type CreateCurrentContractPensionSavingBodySolutionHorizonType,
  type CurrentContractBaseInvestmentToolType,
  type CurrentContractBaseSolutionHorizonType,
  type CurrentContractDetailPensionSaving,
  CurrentContractListItemProposedStepActionType,
  type CurrentSolutionAssetOption,
  PatchCurrentContractPensionSavingBodyInvestmentToolType,
  type PatchCurrentContractPensionSavingBodyProposedStepActionType,
  PensionSavingDetailFundType,
  PensionSavingDetailPensionSavingStrategy,
} from "Infrastructure/ApiZfp/Schema";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { format } from "date-fns";
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useGoalsFulfillmentContext } from "../../GoalsFulfillmentContext";
import { CharacteristicsAccordion } from "../CharacteristicsAccordion";
import type { CurrentSolutionFormHandle } from "./CurrentSolutionWrapperForm";
import { ModifyDepositFields } from "./ModifyDepositFields";

type Props = {
  readonly edit?: boolean;
  readonly investmentToolType: CurrentContractBaseInvestmentToolType;
  readonly institution?: string;
  readonly horizonType: CurrentContractBaseSolutionHorizonType;
  readonly onSuccess?: () => void;
  readonly currentSolutionId?: string;
  readonly initialData?: CurrentContractDetailPensionSaving | null;
};

export const PensionForm = forwardRef<CurrentSolutionFormHandle, Props>(
  ({ investmentToolType, institution, edit, horizonType, onSuccess, currentSolutionId, initialData }, ref) => {
    const { t } = useResource();
    const queryClient = useQueryClient();
    const institutionRef = useRef(institution);
    const { analysisId, versionId } = useContext(AnalysisDetailContext);
    const { assetAllocationOverview } = useGoalsFulfillmentContext();
    const { data: clientsData } = useGetAnalysisClients(analysisId, versionId);

    const { mutate: createMutation } = useCreateCurrentContract();
    const { mutate: updateMutation } = usePatchCurrentContract();

    const clientOptions: Option[] = (clientsData?.clients ?? []).map((client) => ({
      value: client.guid,
      name: `${client.firstName} ${client.surname} (${client.birthDate ? format(new Date(client.birthDate), "dd.MM.yyyy") : ""})`,
    }));

    const proposedStepActionOptions: Option[] = (
      [
        CurrentContractListItemProposedStepActionType.Keep,
        CurrentContractListItemProposedStepActionType.Modify,
        CurrentContractListItemProposedStepActionType.Cancel,
      ] as Array<keyof typeof CurrentContractListItemProposedStepActionType>
    ).map((value) => ({
      value,
      name: t(Resources.AnalysisDetail.GoalsFulfillment.ProposedStepActionTypeOptions[value]),
    }));

    const fundOptions: Option[] = (Object.keys(PensionSavingDetailFundType) as Array<keyof typeof PensionSavingDetailFundType>).map(
      (value) => ({
        value,
        name: t(Resources.AnalysisDetail.GoalsFulfillment.FundOptions[value]),
      }),
    );

    const strategyOptions: Option[] = (
      Object.keys(PensionSavingDetailPensionSavingStrategy) as Array<keyof typeof PensionSavingDetailPensionSavingStrategy>
    ).map((value) => ({
      value,
      name: t(Resources.AnalysisDetail.GoalsFulfillment.StrategyOptions[value]),
    }));

    const methods = useForm<PensionFormType>({
      resolver: yupResolver(getPensionFormSchema(t, assetAllocationOverview?.isPortfolioAssetAllocationRequired)),
      mode: "onChange",
      defaultValues:
        edit && initialData
          ? {
              investmentToolType: initialData.investmentToolType as any,
              institution: initialData.partnerName ?? "",
              clientId: initialData.client?.guid ?? "",
              fund: initialData.pensionSavingDetail?.fundType ?? "",
              strategy: initialData.pensionSavingDetail?.pensionSavingStrategy ?? "",
              currentValue: initialData.oneTimeAmount?.value ?? 0,
              regularAmount: initialData.regularAmount?.value ?? 0,
              horizon: initialData.currentSolutionHorizon ?? 0,
              appreciationRate: initialData.appreciationRate ?? 0,
              employerDeposit: initialData.pensionSavingDetail?.employerContribution?.value ?? 0,
              stateContribution: initialData.pensionSavingDetail?.stateContribution?.value ?? 0,
              managementFee: initialData.managementFee ?? 0,
              equities: initialData.assetAllocation?.equities ?? 0,
              bonds: initialData.assetAllocation?.bonds ?? 0,
              commodities: initialData.assetAllocation?.commodities ?? 0,
              realEstate: initialData.assetAllocation?.realEstate ?? 0,
              cash: initialData.assetAllocation?.cash ?? 0,
              other: initialData.assetAllocation?.others ?? 0,
              proposedStepAction: initialData.proposedStepActionType,
              newDeposit: (initialData.oneTimeAmount?.value ?? 0) + (initialData.oneTimeAmountChange?.value ?? 0),
              newRegularDeposit: (initialData.regularAmount?.value ?? 0) + (initialData.regularAmountChange?.value ?? 0),
              note: initialData.note ?? "",
            }
          : {
              investmentToolType,
              institution,
              clientId: undefined,
            },
    });

    const {
      control,
      watch,
      formState: { errors },
      handleSubmit,
      reset,
      setValue,
      getValues,
    } = methods;

    const proposedStepAction = watch("proposedStepAction");

    useEffect(() => {
      if (edit && initialData) {
        reset({
          investmentToolType: initialData.investmentToolType as any,
          institution: institutionRef.current,
          clientId: initialData.client?.guid ?? "",
          fund: initialData.pensionSavingDetail?.fundType ?? "",
          strategy: initialData.pensionSavingDetail?.pensionSavingStrategy ?? "",
          currentValue: initialData.oneTimeAmount?.value ?? 0,
          regularAmount: initialData.regularAmount?.value ?? 0,
          horizon: initialData.currentSolutionHorizon ?? 0,
          appreciationRate: initialData.appreciationRate ?? 0,
          employerDeposit: initialData.pensionSavingDetail?.employerContribution?.value ?? 0,
          stateContribution: initialData.pensionSavingDetail?.stateContribution?.value ?? 0,
          managementFee: initialData.managementFee ?? 0,
          equities: initialData.assetAllocation?.equities ?? 0,
          bonds: initialData.assetAllocation?.bonds ?? 0,
          commodities: initialData.assetAllocation?.commodities ?? 0,
          realEstate: initialData.assetAllocation?.realEstate ?? 0,
          cash: initialData.assetAllocation?.cash ?? 0,
          other: initialData.assetAllocation?.others ?? 0,
          proposedStepAction: initialData.proposedStepActionType,
          newDeposit: (initialData.oneTimeAmount?.value ?? 0) + (initialData.oneTimeAmountChange?.value ?? 0),
          newRegularDeposit: (initialData.regularAmount?.value ?? 0) + (initialData.regularAmountChange?.value ?? 0),
          note: initialData.note ?? "",
        });
      }
    }, [edit, initialData, reset]);

    useEffect(() => {
      setValue("institution", institution ?? "", { shouldValidate: true });
    }, [institution, setValue]);

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        if (edit) {
          handleSubmit(onEdit)();
        } else {
          handleSubmit(onCreate)();
        }
      },
      prefillFromAssetOption: (asset: CurrentSolutionAssetOption) => {
        setValue("investmentToolType", asset.investmentToolType as any, { shouldValidate: true, shouldDirty: true });
        // TODO: set Client GUID when Assets will be changed from BL BE to ZFP BE
        // setValue("clientId", asset.clientID ?? "", { shouldValidate: true, shouldDirty: true });
        setValue("currentValue", asset.amount?.value ?? 0, { shouldValidate: true, shouldDirty: true });
        setValue("regularAmount", asset.regularAmount?.value ?? 0, { shouldValidate: true, shouldDirty: true });
      },
    }));

    const onCreate = (data: PensionFormType) => {
      const isProposedStepModify = data.proposedStepAction === CurrentContractListItemProposedStepActionType.Modify;
      createMutation(
        {
          analysisId,
          versionId,
          data: {
            investmentToolType: CreateCurrentContractPensionSavingBodyInvestmentToolType.pension_saving,
            solutionHorizonType: horizonType as CreateCurrentContractPensionSavingBodySolutionHorizonType,
            clientGuid: data.clientId,
            proposedStepActionType: data.proposedStepAction as CreateCurrentContractPensionSavingBodyProposedStepActionType,
            partnerName: institution ?? "",
            oneTimeAmount: {
              value: data.currentValue,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.one_time,
            },
            regularAmount: {
              value: data.regularAmount,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.monthly,
            },
            oneTimeAmountChange: isProposedStepModify
              ? {
                  value: (data.newDeposit ?? 0) - data.currentValue,
                  currencyCode: CurrencyCode.CZK,
                  frequency: AmountFrequency.one_time,
                }
              : undefined,
            regularAmountChange: isProposedStepModify
              ? {
                  value: (data.newRegularDeposit ?? 0) - data.regularAmount,
                  currencyCode: CurrencyCode.CZK,
                  frequency: AmountFrequency.monthly,
                }
              : undefined,
            assetAllocation: {
              equities: data.equities ?? 0,
              bonds: data.bonds ?? 0,
              commodities: data.commodities ?? 0,
              realEstate: data.realEstate ?? 0,
              cash: data.cash ?? 0,
              others: data.other ?? 0,
            },
            currentSolutionHorizon: data.horizon ?? 0,
            appreciationRate: data.appreciationRate,
            note: data.note ?? "",
            managementFee: data.managementFee ?? 0,
            pensionSavingDetail: {
              pensionSavingStrategy: data.strategy as any,
              fundType: data.fund as any,
              employerContribution: {
                value: data.employerDeposit ?? 0,
                currencyCode: CurrencyCode.CZK,
                frequency: AmountFrequency.monthly,
              },
              stateContribution: {
                value: data.stateContribution ?? 0,
                currencyCode: CurrencyCode.CZK,
                frequency: AmountFrequency.monthly,
              },
            },
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListCurrentContractsQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
            onSuccess?.();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
          },
        },
      );
    };

    const onEdit = (data: PensionFormType) => {
      if (!currentSolutionId) return;
      const isProposedStepModify = data.proposedStepAction === CurrentContractListItemProposedStepActionType.Modify;
      updateMutation(
        {
          analysisId,
          versionId,
          currentContractGuid: currentSolutionId,
          data: {
            investmentToolType: PatchCurrentContractPensionSavingBodyInvestmentToolType.pension_saving,
            solutionHorizonType: horizonType,
            partnerName: institution ?? "",
            clientGuid: data.clientId,
            proposedStepActionType: data.proposedStepAction as PatchCurrentContractPensionSavingBodyProposedStepActionType,
            oneTimeAmount: {
              value: data.currentValue,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.one_time,
            },
            regularAmount: {
              value: data.regularAmount,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.monthly,
            },
            oneTimeAmountChange: isProposedStepModify
              ? {
                  value: (data.newDeposit ?? 0) - data.currentValue,
                  currencyCode: CurrencyCode.CZK,
                  frequency: AmountFrequency.one_time,
                }
              : undefined,
            regularAmountChange: isProposedStepModify
              ? {
                  value: (data.newRegularDeposit ?? 0) - data.regularAmount,
                  currencyCode: CurrencyCode.CZK,
                  frequency: AmountFrequency.monthly,
                }
              : undefined,
            assetAllocation: {
              equities: data.equities ?? 0,
              bonds: data.bonds ?? 0,
              commodities: data.commodities ?? 0,
              realEstate: data.realEstate ?? 0,
              cash: data.cash ?? 0,
              others: data.other ?? 0,
            },
            currentSolutionHorizon: data.horizon ?? 0,
            appreciationRate: data.appreciationRate,
            note: data.note ?? "",
            managementFee: data.managementFee ?? 0,
            pensionSavingDetail: {
              pensionSavingStrategy: data.strategy as any,
              fundType: data.fund as any,
              employerContribution: {
                value: data.employerDeposit ?? 0,
                currencyCode: CurrencyCode.CZK,
                frequency: AmountFrequency.monthly,
              },
              stateContribution: {
                value: data.stateContribution ?? 0,
                currencyCode: CurrencyCode.CZK,
                frequency: AmountFrequency.monthly,
              },
            },
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListCurrentContractsQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
            onSuccess?.();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
          },
        },
      );
    };

    const handleProposedStepActionChange = (value: string) => {
      if (value === CurrentContractListItemProposedStepActionType.Modify) {
        const currentForm = getValues();
        const initializationMap: Record<string, any> = {
          newDeposit: currentForm.currentValue,
          newRegularDeposit: currentForm.regularAmount,
        };

        for (const [field, sourceValue] of Object.entries(initializationMap)) {
          const currentValue = getValues(field as any);
          if (currentValue === undefined || currentValue === null || currentValue === "" || currentValue === 0) {
            setValue(field as any, sourceValue, {
              shouldDirty: true,
            });
          }
        }
      }
    };

    return (
      <FormProvider {...methods}>
        <form>
          <Stack spacing={Spacing.XXL}>
            <Box>
              <Grid container spacing={Spacing.XL}>
                <Grid size={12}>
                  <FormDropdown
                    control={control}
                    name="clientId"
                    labelEndAdornment={<StyledLabelAdornment />}
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ClientId)}
                    errors={errors}
                    options={clientOptions}
                  />
                </Grid>
                <Grid size={4}>
                  <FormDropdown
                    control={control}
                    name="fund"
                    labelEndAdornment={<StyledLabelAdornment />}
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FundOptions.Title)}
                    errors={errors}
                    options={fundOptions}
                  />
                </Grid>
                <Grid size={4}>
                  <BlFormInput
                    control={control}
                    name="currentValue"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.CurrentValue)}
                    labelEndAdornment={<StyledLabelAdornment />}
                    errors={errors}
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        {t(Resources.Common.CZKSuffix)}
                      </Typography>
                    }
                  />
                </Grid>
                <Grid size={4}>
                  <BlFormInput
                    control={control}
                    name="horizon"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Horizon)}
                    errors={errors}
                    labelEndAdornment={<StyledLabelAdornment />}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        {t(Resources.Common.Years)}
                      </Typography>
                    }
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                      },
                    ]}
                  />
                </Grid>
                <Grid size={4}>
                  <BlFormInput
                    control={control}
                    name="regularAmount"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.RegularAmount)}
                    errors={errors}
                    labelEndAdornment={<StyledLabelAdornment />}
                    inputEndAdornment={
                      <Box sx={{ width: 110 }}>
                        <Typography variant="body2" color="secondary">
                          {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                        </Typography>
                      </Box>
                    }
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                      },
                    ]}
                  />
                </Grid>
                <Grid size={4}>
                  <BlFormInput
                    control={control}
                    name="employerDeposit"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.EmployerDeposit)}
                    errors={errors}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        {t(Resources.Common.CZKSuffix)}
                      </Typography>
                    }
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                      },
                    ]}
                  />
                </Grid>
                <Grid size={4}>
                  <BlFormInput
                    control={control}
                    name="appreciationRate"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.AppreciationRate)}
                    errors={errors}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        %
                      </Typography>
                    }
                    labelEndAdornment={<StyledLabelAdornment />}
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                      },
                    ]}
                  />
                </Grid>

                <Grid size={4}>
                  <FormDropdown
                    control={control}
                    name="strategy"
                    labelEndAdornment={<StyledLabelAdornment />}
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.StrategyOptions.Title)}
                    errors={errors}
                    options={strategyOptions}
                  />
                </Grid>
                <Grid size={4}>
                  <BlFormInput
                    control={control}
                    name="stateContribution"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.StateContribution)}
                    errors={errors}
                    inputEndAdornment={
                      <Box sx={{ width: 110 }}>
                        <Typography variant="body2" color="secondary">
                          {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                        </Typography>
                      </Box>
                    }
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                      },
                    ]}
                  />
                </Grid>
                <Grid size={4}>
                  <BlFormInput
                    control={control}
                    name="managementFee"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ManagementFee)}
                    errors={errors}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        %
                      </Typography>
                    }
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                      },
                    ]}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box>
              <CharacteristicsAccordion<PensionFormType>
                key={assetAllocationOverview?.isPortfolioAssetAllocationRequired ? "open" : "closed"}
                defaultExpanded={assetAllocationOverview?.isPortfolioAssetAllocationRequired}
              />
            </Box>

            <Divider />

            <Box>
              <Grid container spacing={Spacing.XL}>
                <Grid size={12}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t(Resources.AnalysisDetail.GoalsFulfillment.ProposedSolutionSteps)}
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <FormDropdown
                    name="proposedStepAction"
                    options={proposedStepActionOptions}
                    control={control}
                    labelEndAdornment={<StyledLabelAdornment />}
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.ProposedStepActionTypeOptions.Title)}
                    errors={errors}
                    onChange={handleProposedStepActionChange}
                  />
                </Grid>

                {proposedStepAction === CurrentContractListItemProposedStepActionType.Modify && <ModifyDepositFields />}
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Grid container spacing={Spacing.XL}>
                <Grid size={12}>
                  <BlFormInput
                    control={control}
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Note)}
                    placeholder={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.NotePlaceholder)}
                    name="note"
                    errors={errors}
                    multiline
                    minRows={6}
                    maxRows={8}
                    characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </form>
      </FormProvider>
    );
  },
);
